import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useAppSettings } from "../contexts/AppSettingsContext";
import {
  FiHome,
  FiShoppingCart,
  FiList,
  FiPackage,
  FiUsers,
  FiSettings,
  FiLogOut,
  FiClock,
  FiBarChart2,
  FiTag,
  FiTool,
  FiMenu,
  FiX,
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
  FiUserPlus,
  FiSun,
  FiMoon,
  FiCreditCard,
  FiPrinter,
} from "react-icons/fi";
import { MdLocalLaundryService } from "react-icons/md";

const Layout = () => {
  const { user, logout } = useAuth();
  const { appName, appIcon, sidebarDisplay } = useAppSettings();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem("sidebarCollapsed") === "true",
  );
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 769);
  const location = useLocation();
  const [theme, setTheme] = useState(
    () => localStorage.getItem("theme") || "light",
  );

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("theme", theme);
  }, [theme]);

  useEffect(() => {
    localStorage.setItem("sidebarCollapsed", sidebarCollapsed);
  }, [sidebarCollapsed]);

  // Close mobile sidebar on route change
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Track screen size
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 769);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const toggleTheme = () => setTheme((t) => (t === "dark" ? "light" : "dark"));
  const toggleSidebar = () => {
    if (isMobile) {
      setSidebarOpen((o) => !o);
    } else {
      setSidebarCollapsed((c) => !c);
    }
  };

  const isAdmin = user?.role === "admin";
  const isPenyetrika = user?.role === "penyetrika";

  const menuGroups = [
    {
      items: [
        ...(isAdmin
          ? [{ to: "/", icon: <FiHome />, label: "Dashboard", end: true }]
          : []),
        ...(!isPenyetrika
          ? [{ to: "/pos", icon: <FiShoppingCart />, label: "Kasir (POS)" }]
          : []),
      ],
    },
    {
      label: "OPERASIONAL",
      items: [
        { to: "/transactions", icon: <FiList />, label: "Transaksi" },
        ...(!isPenyetrika
          ? [
              { to: "/customers", icon: <FiUsers />, label: "Customer" },
              { to: "/shifts", icon: <FiClock />, label: "Shift" },
              { to: "/printer", icon: <FiPrinter />, label: "Printer" },
            ]
          : []),
      ],
    },
    ...(isAdmin
      ? [
          {
            label: "MASTER DATA",
            items: [
              { to: "/services", icon: <FiTool />, label: "Layanan & Promo" },
              { to: "/inventory", icon: <FiPackage />, label: "Inventaris" },
              {
                to: "/washing-machines",
                icon: <MdLocalLaundryService />,
                label: "Mesin Cuci",
              },
              {
                to: "/payment-methods",
                icon: <FiCreditCard />,
                label: "Metode Pembayaran Khusus",
              },
            ],
          },
          {
            label: "LAINNYA",
            items: [
              { to: "/reports", icon: <FiBarChart2 />, label: "Laporan" },
              { to: "/users", icon: <FiUserPlus />, label: "User" },
              { to: "/settings", icon: <FiSettings />, label: "Pengaturan" },
            ],
          },
        ]
      : []),
  ];

  const isActive = (to, end) => {
    if (end) return location.pathname === to;
    return location.pathname.startsWith(to);
  };

  return (
    <div
      className={`app-layout ${sidebarCollapsed ? "sidebar-collapsed" : ""}`}
    >
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <nav
        className={`sidebar ${sidebarOpen ? "open" : ""} ${sidebarCollapsed ? "collapsed" : ""}`}
      >
        <div className="sidebar-header">
          <div
            className="brand"
            style={{
              justifyContent:
                sidebarDisplay === "icon_only" || sidebarDisplay === "name_only"
                  ? "center"
                  : "flex-start",
            }}
          >
            {(sidebarDisplay === "icon_only" || sidebarDisplay === "both") && (
              <span className="brand-icon">
                {appIcon ? (
                  <img
                    src={appIcon}
                    alt="icon"
                    style={{
                      width: 36,
                      height: 36,
                      objectFit: "contain",
                      borderRadius: 6,
                    }}
                  />
                ) : (
                  "🧺"
                )}
              </span>
            )}
            {(sidebarDisplay === "name_only" || sidebarDisplay === "both") && (
              <span className="brand-text">{appName}</span>
            )}
          </div>
          <button
            className="btn-close-sidebar d-md-none"
            onClick={() => setSidebarOpen(false)}
          >
            <FiX />
          </button>
        </div>

        <div className="sidebar-nav">
          {menuGroups.map((group, gi) => (
            <div key={gi} className="nav-group">
              {group.label && (
                <div className="nav-group-label">{group.label}</div>
              )}
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive: active }) =>
                    `nav-item ${active ? "active" : ""}`
                  }
                  onClick={() => setSidebarOpen(false)}
                  title={sidebarCollapsed ? item.label : undefined}
                >
                  <span className="nav-icon">{item.icon}</span>
                  <span className="nav-label">{item.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </div>

        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar">
              {user?.full_name?.charAt(0)?.toUpperCase()}
            </div>
            <div className="user-details">
              <div className="user-name">{user?.full_name}</div>
              <div className="user-role">{user?.role}</div>
            </div>
          </div>
          <button className="btn-logout" onClick={logout} title="Logout">
            <FiLogOut />
          </button>
        </div>
      </nav>

      {/* Main */}
      <div className="main-content">
        <div className="top-bar">
          <button
            className="btn-menu"
            onClick={toggleSidebar}
            title={isMobile ? "Menu" : sidebarCollapsed ? "Expand" : "Collapse"}
          >
            {isMobile ? (
              <FiMenu size={20} />
            ) : sidebarCollapsed ? (
              <FiChevronRight size={20} />
            ) : (
              <FiChevronLeft size={20} />
            )}
          </button>
          <div className="page-title">
            {menuGroups
              .flatMap((g) => g.items)
              .find((i) => isActive(i.to, i.end))?.label || ""}
          </div>
          <div className="top-bar-right">
            <button
              className="theme-toggle"
              onClick={toggleTheme}
              title={theme === "dark" ? "Light Mode" : "Dark Mode"}
            >
              {theme === "dark" ? <FiSun /> : <FiMoon />}
            </button>
            <span className="d-none d-md-inline text-muted small">
              {new Date().toLocaleDateString("id-ID", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </span>
          </div>
        </div>
        <div className="content-area">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default Layout;
